import React from 'react';
import { Container, Box } from '@mui/material';
import { useTheme } from '../../theme/ThemeProvider';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// The full markdown content for the LIS Integration Guide - using string concatenation to avoid TS issues
const GUIDE_CONTENT = 
"# LIS Integration Guide\n\n" +
"This guide provides comprehensive documentation for integrating Laboratory Information Systems (LIS) with our lab results processing API.\n\n" +
"## Overview\n\n" +
"Our API allows LIS systems to send lab test results for processing and analysis. The API accepts data in a standardized format, processes it using our advanced analysis pipeline, and returns comprehensive results including antimicrobial resistance analysis, drug efficacy scores, and treatment recommendations.\n\n" +
"## Integration Endpoints\n\n" +
"### Single Test Result\n\n" +
"```http\n" +
"POST /api/v1/lis-webhook/standardized/test-results\n" +
"```\n\n" +
"This endpoint accepts a single test result in the standardized format.\n\n" +
"### Batch Test Results\n\n" +
"```http\n" +
"POST /api/v1/lis-webhook/standardized/test-results/batch\n" +
"```\n\n" +
"This endpoint accepts multiple test results in the standardized format.\n\n" +
"### Retrieve PDF Report\n\n" +
"```http\n" +
"GET /api/v1/reports/{test_id}/pdf\n" +
"```\n\n" +
"This endpoint retrieves the generated PDF report for a specific test. Note that the `test_id` parameter should be the original test ID that was sent in the request, not the panel ID returned by the API.\n\n" +
"### Check Processing Status\n\n" +
"```http\n" +
"GET /api/v1/upload-status/{log_entry_id}\n" +
"```\n\n" +
"This endpoint checks the processing status of a submitted test result. The `log_entry_id` is returned in the response when submitting a test result.\n\n" +
"### Retrieve Report Data\n\n" +
"```http\n" +
"GET /api/v1/reports/{test_id}/data\n" +
"```\n\n" +
"This endpoint retrieves the JSON data for a specific test report.\n\n" +
"## Authentication\n\n" +
"All API endpoints require authentication using one of the following methods:\n\n" +
"1. **API Key**: Include your API key in the Authorization header:\n" +
"   ```\n" +
"   Authorization: Bearer your-api-key-here\n" +
"   ```\n\n" +
"2. **Webhook Signature**: For webhook endpoints, include a signature in the X-LIS-Signature header:\n" +
"   ```\n" +
"   X-LIS-Signature: computed-signature-here\n" +
"   ```\n\n" +
"   The signature is computed as:\n" +
"   ```python\n" +
"   import hmac\n" +
"   import hashlib\n" +
"   import json\n" +
"   \n" +
"   # Compute signature\n" +
"   payload = json.dumps(data).encode()\n" +
"   signature = hmac.new(\n" +
"       webhook_secret.encode(),\n" +
"       payload,\n" +
"       hashlib.sha256\n" +
"   ).hexdigest()\n" +
"   ```\n\n" +
"## Standardized Data Format\n\n" +
"The API expects data in the following JSON format:\n\n" +
"```json\n" +
"{\n" +
'  "test_id": "159809",                      // Required: Unique identifier for the test\n' +
'  "panel_type": "RESPIRATORY",                   // Required: Use uppercase (RESPIRATORY, UTI, GI, etc.)\n' +
'  "panel_size": 56,                              // Required: Number of tests in the panel\n' +
'  "patient_info": {                              // Required: Patient information\n' +
'    "patient_id": "56012",                       // Required: Patient ID\n' +
'    "patient_name": "Mcdaniel, Garrick",         // Required: Patient name\n' +
'    "patient_dob": "1995-01-06",                 // Required: ISO format date (YYYY-MM-DD)\n' +
'    "patient_gender": "Male",                    // Required: Male, Female, or Other\n' +
'    "patient_age": "29 years"                    // Optional: Patient age\n' +
"  },\n" +
'  "collection_info": {                           // Required: Collection information\n' +
'    "collection_site": "NASO",                   // Required: Site code\n' +
'    "specimen_type": "Nasopharyngeal swab",      // Required: Type of specimen\n' +
'    "collection_date": "2024-09-10T13:11:00-06:00", // Required: ISO datetime\n' +
'    "received_date": "2024-09-11T10:55:00-06:00"    // Required: ISO datetime\n' +
"  },\n" +
'  "provider_info": {                             // Required: Provider information\n' +
'    "provider_name": "Patel, Satish",            // Required: Provider name\n' +
'    "provider_location": "Main Clinic"           // Optional: Provider location\n' +
"  },\n" +
'  "test_details": {                              // Required: Test details\n' +
'    "viruses": [                                 // Optional: Viral test results\n' +
"      {\n" +
'        "name": "SARS-CoV-2 (N gene)",           // Required: Pathogen name\n' +
'        "result": "Not Detected"                 // Required: "Detected" or "Not Detected"\n' +
"      }\n" +
"    ],\n" +
'    "bacteria": [                                // Optional: Bacterial test results\n' +
"      {\n" +
'        "name": "Bordetella pertussis",          // Required: Pathogen name\n' +
'        "result": "Not Detected"                 // Required: "Detected" or "Not Detected"\n' +
"      }\n" +
"    ],\n" +
'    "resistance_genes": [                        // Optional: Resistance gene results\n' +
"      {\n" +
'        "name": "beta-lactamase CTX-M",          // Required: Gene name\n' +
'        "result": "Detected"                     // Required: "Detected" or "Not Detected"\n' +
"      }\n" +
"    ],\n" +
'    "fungi": [                                   // Optional: Fungal test results\n' +
"      {\n" +
'        "name": "Candida albicans",              // Required: Pathogen name\n' +
'        "result": "Not Detected"                 // Required: "Detected" or "Not Detected"\n' +
"      }\n" +
"    ]\n" +
"  },\n" +
'  "metadata": {                                  // Optional: Additional metadata\n' +
'    "report_status": "Final",                    // Optional: Status (Preliminary, Final, etc.)\n' +
'    "report_date": "2024-09-12T09:18:00-06:00",  // Optional: ISO datetime\n' +
'    "performing_lab": "Main Laboratory",         // Optional: Name of performing lab\n' +
'    "test_method": "PCR"                         // Optional: Test method\n' +
"  }\n" +
"}\n" +
"```\n\n" +
"For more detailed information on integration, please refer to our [API Documentation](/lis-portal/documentation) section.";

const LisIntegrationGuidePage: React.FC = () => {
  const { colors } = useTheme();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ 
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          color: colors.primary.main,
          mt: 3,
          mb: 1.5,
        },
        '& h1': { fontSize: '2rem', borderBottom: `1px solid ${colors.border}`, pb: 1 },
        '& h2': { fontSize: '1.75rem' },
        '& h3': { fontSize: '1.5rem' },
        '& h4': { fontSize: '1.25rem' },
        '& p': { mb: 2 },
        '& a': { color: colors.primary.main },
        '& blockquote': { 
          borderLeft: `4px solid ${colors.primary.light}`,
          pl: 2,
          my: 2,
          color: colors.text.secondary,
        },
        '& table': { 
          width: '100%', 
          borderCollapse: 'collapse',
          mb: 3,
        },
        '& th, & td': { 
          border: `1px solid ${colors.border}`,
          p: 1,
        },
        '& th': { 
          backgroundColor: colors.background.alt,
        },
        '& ul, & ol': { 
          pl: 3,
          mb: 2,
        },
        '& li': { 
          mb: 1,
        },
        '& pre': {
          borderRadius: 1,
          overflow: 'auto',
          mb: 2,
        },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.9em',
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
        }
      }}>
        <ReactMarkdown
          components={{
            code({node, inline, className, children, ...props}) {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <SyntaxHighlighter
                  style={atomDark as any}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            }
          }}
        >
          {GUIDE_CONTENT}
        </ReactMarkdown>
      </Box>
    </Container>
  );
};

export default LisIntegrationGuidePage; 